/**
 * SchedulingCelebration — congratulatory pop-in on the Landing dashboard
 * that fires the FIRST time a user lands here AFTER scheduling an interview.
 *
 * Behavior:
 *   - On mount, fetch /api/interviews. If at least one upcoming interview
 *     exists AND we haven't yet dismissed the celebration for this specific
 *     interview id (tracked in localStorage), show the popup.
 *   - Popup shows the thumbs-up Resmo (delivering portrait) + the interview
 *     headline (role @ company, date) + a 2x2 grid of prep-tool tiles:
 *       · Interviewer Research  → /interviewer-research
 *       · Briefing + Audio      → /interviews/<id> (briefing card)
 *       · Mock Interview        → /mock-interview
 *       · Open this interview   → /interviews/<id>
 *   - Dismissal: Escape key, X button, click outside the popup, or first
 *     interaction with any other dashboard control (any click landing on
 *     an element with [data-dashboard-tool], or any anchor/button click
 *     outside the popup). On dismissal we persist the interview id to
 *     localStorage so the popup never re-fires for the same interview.
 *
 * Renders nothing when there are no upcoming interviews or the latest one
 * has already been celebrated.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, Search, Mic, Mic2, Calendar, Sparkles, ChevronRight, CalendarPlus } from "lucide-react";
import { api } from "../../lib/api";
import ResmoMascot from "../ResmoMascot";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LS_KEY = "resmo.interview-celebrated";

function getCelebratedIds() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}
function rememberCelebrated(id) {
  try {
    const cur = getCelebratedIds();
    cur.add(id);
    localStorage.setItem(LS_KEY, JSON.stringify([...cur]));
  } catch { /* localStorage disabled, no-op */ }
}

function relativeWhen(dateIso) {
  const d = new Date(dateIso);
  if (isNaN(d.getTime())) return "soon";
  const diffMs = d.getTime() - Date.now();
  const days = Math.floor(diffMs / 86_400_000);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days > 0 && days < 7) return `in ${days} days`;
  return d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
}

export default function SchedulingCelebration() {
  const navigate = useNavigate();
  const [interview, setInterview] = useState(null);
  const [open, setOpen] = useState(false);
  const [fading, setFading] = useState(false);

  // Step 1: on mount, fetch upcoming interviews and pick the newest one we
  // haven't celebrated yet. Skip if the dashboard onboarding tour hasn't
  // been completed yet — otherwise both overlays mount on first visit and
  // visually stack. The tour persists `resmo:tutorial:dashboard:done`=`1`
  // when finished; until then we defer the celebration to the next dashboard
  // visit (the user won't lose anything — popup re-checks on every mount).
  useEffect(() => {
    let alive = true;
    (async () => {
      let tourDone = "1";
      try { tourDone = window.localStorage.getItem("resmo:tutorial:dashboard:done"); } catch { /* noop */ }
      if (tourDone !== "1") return;
      try {
        const { data } = await api.get("/interviews");
        if (!alive) return;
        const upcoming = (data.upcoming || []).filter((iv) => !!iv.interview_at);
        if (upcoming.length === 0) return;
        const celebrated = getCelebratedIds();
        // Most recently *created* upcoming interview that hasn't been celebrated.
        const candidates = upcoming
          .filter((iv) => !celebrated.has(iv.id))
          .sort((a, b) => {
            const ca = new Date(a.created_at || a.interview_at).getTime();
            const cb = new Date(b.created_at || b.interview_at).getTime();
            return cb - ca;
          });
        if (candidates.length === 0) return;
        setInterview(candidates[0]);
        setOpen(true);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  const dismiss = useCallback(() => {
    if (interview?.id) rememberCelebrated(interview.id);
    // Quick fade so the dissolve feels intentional rather than abrupt.
    setFading(true);
    setTimeout(() => {
      setOpen(false);
      setFading(false);
    }, 250);
  }, [interview]);

  // Dismiss on Escape key + on first interaction with any non-popup control.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    const onClick = (e) => {
      // If the click already landed inside the popup, ignore (interactions
      // INSIDE the popup are part of the celebration UI itself).
      if (e.target.closest("[data-celebration-popup]")) return;
      // Any other actionable element on the dashboard dismisses.
      if (e.target.closest("a, button, input, select, textarea, [data-dashboard-tool]")) {
        dismiss();
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("click", onClick, true);
    };
  }, [open, dismiss]);

  const tiles = useMemo(() => {
    if (!interview) return [];
    // Wrap each navigate() with dismiss() so picking a tile both flips the
    // dismissal flag (persists to localStorage) AND routes the user — the
    // popup won't re-fire for this interview on the next dashboard visit.
    const go = (path) => () => { dismiss(); navigate(path); };
    return [
      {
        id: "open",
        icon: Calendar,
        label: "Open this interview",
        sub: "Countdown · briefing · reminders",
        bg: "linear-gradient(135deg, #1E40AF 0%, #0E5A4A 100%)",
        onClick: go(`/interviews/${interview.id}`),
      },
      {
        id: "researcher",
        icon: Search,
        label: "Research your interviewer",
        sub: "LinkedIn-backed dossier in 30s",
        bg: "linear-gradient(135deg, #FF6A00 0%, #B91C1C 100%)",
        onClick: go("/interviewer-research"),
      },
      {
        id: "briefing",
        icon: Mic2,
        label: "Generate the 5-min briefing",
        sub: "Listen on your way in",
        bg: "linear-gradient(135deg, #FBBF24 0%, #CA8A04 100%)",
        onClick: go(`/interviews/${interview.id}#briefing`),
      },
      {
        id: "mock",
        icon: Mic,
        label: "Dry-run mock interview",
        sub: "Voice or text · scored",
        bg: "linear-gradient(135deg, #0E5A4A 0%, #20C1A7 100%)",
        onClick: go("/mock-interview"),
      },
    ];
  }, [interview, navigate, dismiss]);

  if (!open || !interview) return null;

  const date = new Date(interview.interview_at);
  const when = relativeWhen(interview.interview_at);
  // API returns role_title / role_company (Pydantic model fields); we also
  // tolerate plain `role` / `company` for legacy responses.
  const company = interview.role_company || interview.company || "";
  const role = interview.role_title || interview.role || "Your next interview";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{
        background: "rgba(15,27,45,0.55)",
        backdropFilter: "blur(3px)",
        opacity: fading ? 0 : 1,
        transition: "opacity 0.25s ease",
      }}
      data-testid="scheduling-celebration-backdrop"
      onClick={(e) => {
        // Backdrop click dismisses; clicks inside the card are stopped below.
        if (e.target === e.currentTarget) dismiss();
      }}
    >
      <div
        data-celebration-popup
        data-testid="scheduling-celebration-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="scheduling-celebration-title"
        className="rm-card-static w-full max-w-xl overflow-hidden relative"
        style={{
          background: "white",
          borderColor: "#0F1B2D",
          borderWidth: 3,
          boxShadow: "8px 8px 0 0 #FFC145, 0 24px 60px rgba(15,27,45,0.4)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close X */}
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-2 right-2 rm-btn rm-btn-ghost p-1.5"
          aria-label="Dismiss celebration"
          data-testid="scheduling-celebration-close"
        >
          <X size={14} />
        </button>

        {/* Banner with mascot */}
        <div
          className="px-5 pt-4 pb-2 flex items-center gap-3"
          style={{ background: "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)" }}
        >
          <div style={{ flexShrink: 0 }}>
            <ResmoMascot state="satisfied" flavor="resume" size={84} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <Sparkles size={13} style={{ color: "#CA8A04" }} />
              <span className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#CA8A04", fontWeight: 700 }}>
                Interview Scheduled
              </span>
            </div>
            <h2
              id="scheduling-celebration-title"
              className="font-display uppercase leading-tight"
              style={{ fontSize: "1.4rem", color: "#0F1B2D", letterSpacing: "-0.02em" }}
            >
              Nice work — you're in!
            </h2>
            <div className="text-[13px] mt-1 leading-snug" style={{ color: "#0F1B2D" }}>
              <b>{role}</b>{company ? <> @ <b>{company}</b></> : null} · {when}
              <span className="font-mono-rm text-[10px] uppercase ml-1.5" style={{ color: "#475569" }}>
                ({date.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })})
              </span>
            </div>
          </div>
        </div>

        {/* Action prompt */}
        <div className="px-5 pt-4 pb-2">
          <div className="font-mono-rm text-[11px] uppercase tracking-widest mb-2" style={{ color: "#0F1B2D", fontWeight: 700 }}>
            · Use the next few days well — Resmo's got tools
          </div>
        </div>

        {/* 2x2 tool grid */}
        <div className="px-5 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {tiles.map((tile) => {
            const Icon = tile.icon;
            return (
              <button
                key={tile.id}
                type="button"
                onClick={tile.onClick}
                className="text-left rounded-md p-3 flex items-start gap-2 transition-transform hover:translate-y-[-2px]"
                style={{
                  background: tile.bg,
                  color: "white",
                  border: "2px solid #0F1B2D",
                  boxShadow: "3px 3px 0 0 #0F1B2D",
                }}
                data-testid={`scheduling-celebration-tile-${tile.id}`}
              >
                <Icon size={18} className="flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-display uppercase text-[12px] leading-tight" style={{ letterSpacing: "-0.01em" }}>
                    {tile.label}
                  </div>
                  <div className="text-[11px] mt-1 leading-snug" style={{ opacity: 0.85 }}>
                    {tile.sub}
                  </div>
                </div>
                <ChevronRight size={14} className="flex-shrink-0 mt-1" />
              </button>
            );
          })}
        </div>

        {/* Add to Calendar (.ics) — primary "save it now" CTA. The .ics URL
            comes back signed on the interview payload so this is just a
            standard anchor download — no fetch/blob needed. */}
        {interview.ics_url && (
          <div className="px-5 pb-5">
            <a
              href={`${BACKEND_URL}${interview.ics_url}`}
              download={`resmo-interview-${(interview.id || "").slice(0, 8)}.ics`}
              onClick={(e) => { e.stopPropagation(); }}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 font-display uppercase text-[12px] tracking-widest transition-transform hover:translate-y-[-1px]"
              style={{
                background: "#FFC145",
                color: "#0F1B2D",
                border: "2px solid #0F1B2D",
                textDecoration: "none",
                fontWeight: 700,
                letterSpacing: "0.06em",
                boxShadow: "3px 3px 0 0 #0F1B2D",
              }}
              data-testid="scheduling-celebration-ics"
            >
              <CalendarPlus size={14} />
              Add to your calendar (.ics)
            </a>
            <div className="text-center mt-1.5 font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
              Opens in Apple / Google / Outlook · includes 7-day · 24h · 1h reminders
            </div>
          </div>
        )}

        {/* Footer dismiss hint */}
        <div
          className="px-5 py-2 text-center font-mono-rm text-[10px] uppercase tracking-widest"
          style={{ background: "#0F1B2D", color: "#FFC145", fontWeight: 700 }}
        >
          Click anywhere outside or pick a tool to dismiss
        </div>
      </div>
    </div>
  );
}
